import {
    Button,
    DialogTitle,
    Select,
    Text,
    Flex,
    ToggleSwitch,
    PriorityIcon
} from '@priority-software/priority-style-react';
import React, {Component} from 'react';
import {DialogModal} from '../../../../../_shared/componnets/Dialog/Dialog';
import {returnFocus, setIsNavigationEnabled} from '../../../../../_shared/GWTBridge/invoke/genPage';
import {setIsReactPopupOpen} from '../../../../../_shared/GWTBridge/invoke/netForm';
import {getTopDialogZindex} from '../../../../../_shared/GWTBridge/invoke/priModalDlg';
import {getLocalizedText, texts} from '../../../../../constants/TextConst';
import {formsStore} from '../../../../../_legacy/Form/stores/FormsStore';
import {FormLabel} from '../../../../../_legacy/Form/stores/Form';

import {
    setLabelsState,
    initFormLabels,
    printLabels,
    setLabelsStorage,
    setLabelSettings
} from '../../actions/labels';
import {showHelpMessage} from '../../../../../_shared/Messages/actions/MessageActions';
import {keyCodes} from '../../../../../constants/index';

import styles from '../../styles/labelSettings.module.scss';
import {Footer} from './ModalFooter';

const labelSettingsModalRef: React.RefObject<LabelSettingsModal> = React.createRef();

export async function renderLabelSettingsModal(root) {
    root.render(<LabelSettingsModal ref={labelSettingsModalRef} />);
    return labelSettingsModalRef;
}

interface LabelSettingsModalState {
    isOpen: boolean;
    items: FormLabel[];
    selectedLabel: FormLabel;
    isClear: boolean;
    isAll: boolean;
    isDirty: boolean;
    mayPrint: boolean;
}

export class LabelSettingsModal extends Component<any, LabelSettingsModalState> {
    defaultState: LabelSettingsModalState = {
        items: [],
        isOpen: false,
        selectedLabel: null,
        isClear: true,
        isAll: true,
        mayPrint: false,
        isDirty: false
    };

    state = {
        ...this.defaultState
    };
    allHelpButtonRef: any = React.createRef();
    clearHelpButtonRef: any = React.createRef();
    printHelpButtonRef: any = React.createRef();
    modalHelpRef: any = React.createRef();

    itemsRef = [];

    showDialog = async () => {
        await initFormLabels(true);
        await setLabelsState();

        window.addEventListener('keydown', this.handleKeyDown, true);

        const formLabels = formsStore.currentForm.formLabels;
        this.setState({
            items: formLabels.labels,
            selectedLabel: formLabels.selected,
            isClear: formLabels.clean === '1',
            isAll: formLabels.lines === '1',
            mayPrint: formLabels.print === '1',
            isOpen: true
        });

        setIsReactPopupOpen(true);
        setIsNavigationEnabled(false);
    };

    handlePrint = async () => {
        const {isAll, isClear, selectedLabel, mayPrint} = this.state;

        setLabelSettings(
            selectedLabel.value,
            isAll ? '1' : '0',
            isClear ? '1' : '0',
            formsStore.currentForm.formLabels.print
        );

        await printLabels(false, mayPrint);
        this.handleClose();
    };

    handleSave = async () => {
        const {isAll, isClear, selectedLabel, mayPrint} = this.state;

        await setLabelsStorage(
            selectedLabel.value,
            isAll ? '1' : '0',
            isClear ? '1' : '0',
            mayPrint ? '1' : '0'
        );

        this.handleClose();
    };

    handleClose = () => {
        window.removeEventListener('keydown', this.handleKeyDown, true);

        this.setState({
            ...this.defaultState
        });
        setIsReactPopupOpen(false);
        setIsNavigationEnabled(true);

        returnFocus();
    };

    handleKeyDown = e => {
        const name = document.activeElement.getAttribute('name');

        switch (e.keyCode) {
            case keyCodes.ENTER:
                e.stopPropagation();
                if (name === 'print') this.handlePrint();
                if (name === 'save') this.handleSave();
                break;
        }
    };

    renderTitle = () => {
        return (
            <Flex justifyContent="space-between" componentClasses={styles.title}>
                <DialogTitle
                    title={getLocalizedText(texts.printLabelSettingsTitle)}
                    size="large"
                    textColor="primary-700"
                />
                <Flex justifyContent="flex-end" componentClasses={styles.titleHelp}>
                    <Button
                        onClick={() => {
                            showHelpMessage(getLocalizedText(texts.printLabelDialogHelp));
                        }}
                        prefixIcon={PriorityIcon.questionCircle}
                        size="small"
                        skin="text"
                        name="help"
                        inputRef={this.modalHelpRef}
                    >
                        {getLocalizedText(texts.help)}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    renderContent = () => {
        const {items, isAll, isClear, selectedLabel, mayPrint} = this.state;
        const {formLabels} = formsStore.currentForm;
        return (
            <div>
                <Text
                    componentClasses={styles.itemMargin}
                    textColor="primary-700"
                    skin="subtitle-1"
                >
                    {getLocalizedText(texts.printLabelSelectTitle)}
                </Text>
                <Select
                    defaultValue={selectedLabel}
                    onChange={(label: any) => {
                        this.setState({selectedLabel: label, isDirty: true});
                    }}
                    options={items}
                />
                <div className={styles.itemMargin}>
                    <Flex justifyContent="flex-start">
                        <div className={styles.labelButton}>
                            <ToggleSwitch
                                componentClasses={styles.labelButton}
                                tabIndex={-1}
                                checked={isClear}
                                onChange={() => {
                                    this.setState({isClear: !isClear, isDirty: true});
                                }}
                            />
                        </div>
                        <Text
                            componentClasses={styles.itemMargin}
                            textColor="primary-700"
                            skin="caption-1"
                        >
                            {getLocalizedText(texts.labelClearPrev)}
                        </Text>
                        <Button
                            onClick={() => {
                                showHelpMessage(getLocalizedText(texts.clearLastHelp));
                            }}
                            prefixIcon={PriorityIcon.questionCircle}
                            size="small"
                            skin="text"
                            name="help"
                            inputRef={this.clearHelpButtonRef}
                        />
                    </Flex>
                    <Flex justifyContent="flex-start">
                        <div className={styles.labelButton}>
                            <ToggleSwitch
                                componentClasses={styles.labelButton}
                                tabIndex={-1}
                                checked={isAll}
                                onChange={() => {
                                    this.setState({isAll: !isAll, isDirty: true});
                                }}
                            />
                        </div>
                        <Text
                            componentClasses={styles.itemMargin}
                            textColor="primary-700"
                            skin="caption-1"
                        >
                            {getLocalizedText(texts.labelAllRecords)}
                        </Text>
                        <Button
                            onClick={() => {
                                showHelpMessage(getLocalizedText(texts.allRecordsHelp));
                            }}
                            prefixIcon={PriorityIcon.questionCircle}
                            size="small"
                            skin="text"
                            name="help"
                            inputRef={this.allHelpButtonRef}
                        />
                    </Flex>
                    {formLabels.mayprint === '1' && (
                        <Flex justifyContent="flex-start">
                            <div className={styles.labelButton}>
                                <ToggleSwitch
                                    componentClasses={styles.labelButton}
                                    tabIndex={-1}
                                    checked={mayPrint}
                                    onChange={() => {
                                        this.setState({mayPrint: !mayPrint, isDirty: true});
                                    }}
                                />
                            </div>
                            <Text
                                componentClasses={styles.itemMargin}
                                textColor="primary-700"
                                skin="caption-1"
                            >
                                {getLocalizedText(texts.printImmediately)}
                            </Text>
                            <Button
                                onClick={() => {
                                    showHelpMessage(getLocalizedText(texts.printImmediatelyHelp));
                                }}
                                prefixIcon={PriorityIcon.questionCircle}
                                size="small"
                                skin="text"
                                name="help"
                                inputRef={this.printHelpButtonRef}
                            />
                        </Flex>
                    )}
                </div>
                <Footer
                    handlePrint={this.handlePrint}
                    handleSave={this.handleSave}
                    state={this.state}
                />
            </div>
        );
    };

    render() {
        const {isOpen} = this.state;

        return (
            isOpen && (
                <DialogModal
                    isDialogOpen={isOpen}
                    content={this.renderContent()}
                    onClose={this.handleClose}
                    title={this.renderTitle()}
                    size={'large'}
                    draggable={true}
                    zIndex={getTopDialogZindex()}
                />
            )
        );
    }
}
